import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  ArrowLeft,
  MessageSquare,
  Plus,
  Edit,
  Trash2,
  Star,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TestimonialsManagementPage = () => {
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Selected testimonial
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    text: '',
    rating: 5,
    enabled: true
  });
  
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    verifyAdminAndFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyAdminAndFetch = async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      await axios.get(`${BACKEND_URL}/api/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchTestimonials();
    } catch (err) {
      navigate('/admin/login');
    }
  };

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/testimonials/`);
      setTestimonials(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching testimonials:', err);
      toast.error('Failed to load testimonials');
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      role: '',
      text: '',
      rating: 5,
      enabled: true
    });
  };

  const handleAddTestimonial = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleEditTestimonial = (testimonial) => {
    setSelectedTestimonial(testimonial);
    setFormData({
      name: testimonial.name,
      role: testimonial.role,
      text: testimonial.text,
      rating: testimonial.rating,
      enabled: testimonial.enabled
    });
    setShowEditModal(true);
  };

  const handleDeleteClick = (testimonial) => {
    setSelectedTestimonial(testimonial);
    setShowDeleteModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return false;
    }
    if (!formData.role.trim()) {
      toast.error('Role is required');
      return false;
    }
    if (!formData.text.trim()) {
      toast.error('Testimonial text is required');
      return false;
    }
    if (formData.rating < 1 || formData.rating > 5) {
      toast.error('Rating must be between 1 and 5');
      return false;
    }
    return true;
  };

  const handleSubmitAdd = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setSubmitting(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/testimonials/`, {
        name: formData.name,
        role: formData.role,
        text: formData.text,
        rating: parseInt(formData.rating),
        enabled: formData.enabled
      });

      toast.success('Testimonial added successfully!');
      setShowAddModal(false);
      resetForm();
      await fetchTestimonials();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to add testimonial';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setSubmitting(true);
    
    try {
      await axios.put(`${BACKEND_URL}/api/testimonials/${selectedTestimonial.id}`, {
        name: formData.name,
        role: formData.role,
        text: formData.text,
        rating: parseInt(formData.rating),
        enabled: formData.enabled
      });

      toast.success('Testimonial updated successfully!');
      setShowEditModal(false);
      setSelectedTestimonial(null);
      await fetchTestimonials();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to update testimonial';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedTestimonial) return;

    setSubmitting(true);
    
    try {
      await axios.delete(`${BACKEND_URL}/api/testimonials/${selectedTestimonial.id}`);
      toast.success('Testimonial deleted successfully!');
      setShowDeleteModal(false);
      setSelectedTestimonial(null);
      await fetchTestimonials();
    } catch (error) {
      toast.error('Failed to delete testimonial');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleEnabled = async (testimonial) => {
    try {
      await axios.put(`${BACKEND_URL}/api/testimonials/${testimonial.id}`, {
        enabled: !testimonial.enabled
      });
      toast.success(`Testimonial ${!testimonial.enabled ? 'enabled' : 'disabled'} successfully!`);
      await fetchTestimonials();
    } catch (error) {
      toast.error('Failed to update testimonial status');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading testimonials...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-md border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin/dashboard')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden sm:inline">Back</span>
              </Button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Testimonials Management</h1>
                <p className="text-sm text-gray-600">{testimonials.length} testimonials</p>
              </div>
            </div>
            <Button
              onClick={handleAddTestimonial}
              className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Testimonial
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg mb-2">No testimonials found</p>
              <p className="text-sm text-gray-400">Add your first testimonial to get started</p>
            </div>
          ) : (
            testimonials.map((testimonial) => (
              <Card 
                key={testimonial.id}
                className={`relative ${testimonial.enabled ? 'border-green-200 bg-white' : 'border-gray-200 bg-gray-50 opacity-60'} hover:shadow-lg transition-all`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg">{testimonial.name}</CardTitle>
                      <p className="text-sm text-gray-600 mt-1">{testimonial.role}</p>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-semibold ${testimonial.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {testimonial.enabled ? 'Enabled' : 'Disabled'}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Star Rating */}
                  <div className="flex gap-1 mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>

                  <p className="text-gray-700 text-sm italic mb-4 line-clamp-3">
                    "{testimonial.text}"
                  </p>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => toggleEnabled(testimonial)}
                      className="flex-1"
                    >
                      {testimonial.enabled ? <EyeOff className="h-3 w-3 mr-1" /> : <Eye className="h-3 w-3 mr-1" />}
                      {testimonial.enabled ? 'Disable' : 'Enable'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditTestimonial(testimonial)}
                      className="border-blue-600 text-blue-600 hover:bg-blue-50"
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteClick(testimonial)}
                      className="border-red-600 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Add Testimonial Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-green-600" />
              Add New Testimonial
            </DialogTitle>
            <DialogDescription>
              Create a new testimonial to display on the homepage
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitAdd} className="space-y-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Name */}
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  placeholder="Enter name"
                  required
                />
              </div>

              {/* Role */}
              <div>
                <Label htmlFor="role">Role *</Label>
                <Input
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleFormChange}
                  placeholder="e.g., Parent of Class 5 Student"
                  required
                />
              </div>

              {/* Rating */}
              <div>
                <Label htmlFor="rating">Rating (1-5) *</Label>
                <Input
                  id="rating"
                  name="rating"
                  type="number"
                  min="1"
                  max="5"
                  value={formData.rating}
                  onChange={handleFormChange}
                  required
                />
              </div>

              {/* Enabled */}
              <div className="flex items-center gap-2 pt-6">
                <input
                  id="enabled"
                  name="enabled"
                  type="checkbox"
                  checked={formData.enabled}
                  onChange={handleFormChange}
                  className="h-4 w-4 text-blue-600 rounded"
                />
                <Label htmlFor="enabled" className="cursor-pointer">Show on homepage</Label>
              </div>
            </div>

            {/* Testimonial Text */}
            <div>
              <Label htmlFor="text">Testimonial Text *</Label>
              <Textarea
                id="text"
                name="text"
                value={formData.text}
                onChange={handleFormChange}
                placeholder="Enter the testimonial text..."
                rows={4}
                required
              />
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddModal(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-green-600 hover:bg-green-700"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : 'Save Testimonial'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Testimonial Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-blue-600" />
              Edit Testimonial
            </DialogTitle>
            <DialogDescription>
              Update the testimonial details
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitEdit} className="space-y-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Name */}
              <div>
                <Label htmlFor="edit_name">Name *</Label>
                <Input
                  id="edit_name"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  placeholder="Enter name"
                  required
                />
              </div>

              {/* Role */}
              <div>
                <Label htmlFor="edit_role">Role *</Label>
                <Input
                  id="edit_role"
                  name="role"
                  value={formData.role}
                  onChange={handleFormChange}
                  placeholder="e.g., Parent of Class 5 Student"
                  required
                />
              </div>

              {/* Rating */}
              <div>
                <Label htmlFor="edit_rating">Rating (1-5) *</Label>
                <Input
                  id="edit_rating"
                  name="rating"
                  type="number"
                  min="1"
                  max="5"
                  value={formData.rating}
                  onChange={handleFormChange}
                  required
                />
              </div>

              {/* Enabled */}
              <div className="flex items-center gap-2 pt-6">
                <input
                  id="edit_enabled"
                  name="enabled"
                  type="checkbox"
                  checked={formData.enabled}
                  onChange={handleFormChange}
                  className="h-4 w-4 text-blue-600 rounded"
                />
                <Label htmlFor="edit_enabled" className="cursor-pointer">Show on homepage</Label>
              </div>
            </div>

            {/* Testimonial Text */}
            <div>
              <Label htmlFor="edit_text">Testimonial Text *</Label>
              <Textarea
                id="edit_text"
                name="text"
                value={formData.text}
                onChange={handleFormChange}
                placeholder="Enter the testimonial text..."
                rows={4}
                required
              />
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditModal(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700"
                disabled={submitting}
              >
                {submitting ? 'Updating...' : 'Update Testimonial'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Confirm Deletion
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this testimonial?
            </DialogDescription>
          </DialogHeader>
          {selectedTestimonial && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4">
              <p className="text-sm font-semibold text-red-900 mb-2">
                {selectedTestimonial.name}
              </p>
              <p className="text-sm text-red-700 mb-1">
                {selectedTestimonial.role}
              </p>
              <p className="text-xs text-red-600 italic mt-2">
                "{selectedTestimonial.text.substring(0, 100)}..."
              </p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-red-600 hover:bg-red-700"
              onClick={handleConfirmDelete}
              disabled={submitting}
            >
              {submitting ? 'Deleting...' : 'Delete Testimonial'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TestimonialsManagementPage;
